import React, { useCallback, useId } from 'react';
import { useThemeColors } from '@brandclub/bc-widgets';

export type ControlBarMode = 'connecting' | 'ending' | 'live';

export interface ControlBarProps {
  mode: ControlBarMode;
  /** Controlled text field value. */
  value: string;
  onChange: (value: string) => void;
  /** Submit typed text (Enter or Send button). */
  onSubmit: () => void;
  muted: boolean;
  onToggleMute: () => void;
  onEnd: () => void;
  /** True while user or assistant audio is active (waveform animation). */
  active: boolean;
  /** True while a tool round-trip is in flight (waveform animation). */
  toolActive?: boolean;
  /** Disable mic mute until the call is live. */
  micDisabled?: boolean;
}

const BAR_MAX_WIDTH = 768;
const BAR_HEIGHT = 54;
const FOOTER_MIN_HEIGHT = 88;

/**
 * Voice call bottom bar — Figma `4936:3489` (desktop home chat input).
 * Text field + mic mute + brand-colored End / Cancel / Send pill.
 */
export const ControlBar: React.FC<ControlBarProps> = ({
  mode,
  value,
  onChange,
  onSubmit,
  muted,
  onToggleMute,
  onEnd,
  active,
  toolActive = false,
  micDisabled = false,
}) => {
  const inputId = useId();
  const { themeColors } = useThemeColors();
  const buttonBg = themeColors.primary || '#000000';
  const buttonFg = themeColors.buttonText || '#FFFFFF';
  const textFg = themeColors.textPrimary || '#000000';

  const hasText = value.trim().length > 0;
  const waveformActive = active || toolActive;
  const inputDisabled = mode !== 'live';
  const showSend = mode === 'live' && hasText;
  const isPending = mode === 'connecting' || mode === 'ending';
  const actionDisabled = mode === 'ending';

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey && mode === 'live' && hasText) {
        e.preventDefault();
        onSubmit();
      }
    },
    [hasText, mode, onSubmit],
  );

  const handleActionClick = useCallback(() => {
    if (actionDisabled) return;
    if (showSend) {
      onSubmit();
      return;
    }
    onEnd();
  }, [actionDisabled, onEnd, onSubmit, showSend]);

  return (
    <footer
      style={{
        position: 'relative',
        zIndex: 3,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: FOOTER_MIN_HEIGHT,
        padding: '0 16px',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        background: 'transparent',
      }}
    >
      <style>{CONTROL_BAR_CSS}</style>

      <div
        className="voice-input-bar"
        style={{
          width: '100%',
          maxWidth: BAR_MAX_WIDTH,
          height: BAR_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: '0 16px',
          background: 'var(--voice-bar-bg, #FFFFFF)',
          border: '1px solid #DCDDDC',
          borderRadius: 9999,
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05)',
        }}
      >
        <input
          id={inputId}
          type="text"
          className="voice-input-bar-field"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={inputDisabled}
          placeholder="Type or say anything"
          aria-label="Type or say anything"
          autoComplete="off"
          style={{
            flex: 1,
            minWidth: 0,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontSize: 16,
            lineHeight: '22px',
            fontWeight: 500,
            color: textFg,
            fontFamily: 'inherit',
          }}
        />

        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 16,
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={onToggleMute}
            disabled={micDisabled}
            aria-pressed={muted}
            aria-label={muted ? 'Unmute microphone' : 'Mute microphone'}
            className="voice-input-bar-mic"
            style={{
              appearance: 'none',
              border: 'none',
              background: 'transparent',
              padding: 0,
              width: 24,
              height: 24,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: micDisabled ? 'not-allowed' : 'pointer',
              opacity: micDisabled ? 0.45 : 1,
              color: textFg,
            }}
          >
            {muted ? <MicMutedIcon /> : <MicIcon color={textFg} />}
          </button>

          <button
            type="button"
            onClick={handleActionClick}
            disabled={actionDisabled}
            aria-label={
              showSend
                ? 'Send message'
                : mode === 'connecting'
                  ? 'Cancel call'
                  : mode === 'ending'
                    ? 'Ending call'
                    : 'End call'
            }
            className="voice-input-bar-action"
            style={{
              appearance: 'none',
              border: 'none',
              background: buttonBg,
              color: buttonFg,
              borderRadius: 24,
              minHeight: 40,
              padding: showSend ? '8px 12px' : '8px 16px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              fontSize: 16,
              lineHeight: '22px',
              fontWeight: 500,
              fontFamily: 'inherit',
              cursor: actionDisabled ? 'default' : 'pointer',
              transition: 'transform 80ms ease, opacity 120ms ease',
            }}
          >
            {isPending ? (
              <>
                <SpinnerIcon color={buttonFg} />
                <span className="voice-input-bar-action-label" style={{ color: buttonFg }}>
                  {mode === 'ending' ? 'Ending' : 'Cancel'}
                </span>
              </>
            ) : showSend ? (
              <SendIcon color={buttonFg} />
            ) : (
              <>
                <VoiceBarsIcon active={waveformActive} color={buttonFg} />
                <span className="voice-input-bar-action-label" style={{ color: buttonFg }}>
                  End
                </span>
              </>
            )}
          </button>
        </div>
      </div>
    </footer>
  );
};

/** Figma mic asset (`4936:3493`) — 17×24 solid microphone, brand text color. */
const MicIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="17" height="24" viewBox="0 0 17 24" fill="none" aria-hidden>
    <path
      d="M8.5 17.1429C5.68778 17.1429 3.4 14.8359 3.4 12V5.14286C3.4 2.307 5.68778 0 8.5 0C11.3122 0 13.6 2.307 13.6 5.14286V12C13.6 14.8359 11.3122 17.1429 8.5 17.1429ZM8.5 1.71429C6.62533 1.71429 5.1 3.25243 5.1 5.14286V12C5.1 13.8904 6.62533 15.4286 8.5 15.4286C10.3747 15.4286 11.9 13.8904 11.9 12V5.14286C11.9 3.25243 10.3747 1.71429 8.5 1.71429ZM16.15 10.2857C15.6806 10.2857 15.3 10.6695 15.3 11.1429V12C15.3 15.7811 12.2496 18.8571 8.5 18.8571C4.75044 18.8571 1.7 15.7811 1.7 12V11.1429C1.7 10.6695 1.31941 10.2857 0.85 10.2857C0.380587 10.2857 0 10.6695 0 11.1429V12C0 16.4372 3.36069 20.0966 7.65 20.5279V22.2857H5.95C5.48059 22.2857 5.1 22.6695 5.1 23.1429C5.1 23.6162 5.48059 24 5.95 24H11.05C11.5194 24 11.9 23.6162 11.9 23.1429C11.9 22.6695 11.5194 22.2857 11.05 22.2857H9.35V20.5279C13.6393 20.0964 17 16.437 17 12V11.1429C17 10.6695 16.6194 10.2857 16.15 10.2857Z"
      fill={color}
    />
  </svg>
);

/**
 * Figma muted mic asset (`5018:2577`) — 20.75×24, hardcoded red `#FF534A`
 * with a diagonal slash. Color is fixed per the mock regardless of brand.
 */
const MicMutedIcon: React.FC = () => (
  <svg width="20" height="24" viewBox="0 0 20.75 24" fill="none" aria-hidden>
    <mask id="voice-mic-muted-mask" fill="white">
      <path d="M10.5 17.1429C7.68778 17.1429 5.4 14.8359 5.4 12V5.14286C5.4 2.307 7.68778 0 10.5 0C13.3122 0 15.6 2.307 15.6 5.14286V12C15.6 14.8359 13.3122 17.1429 10.5 17.1429ZM10.5 1.71429C8.62533 1.71429 7.1 3.25243 7.1 5.14286V12C7.1 13.8904 8.62533 15.4286 10.5 15.4286C12.3747 15.4286 13.9 13.8904 13.9 12V5.14286C13.9 3.25243 12.3747 1.71429 10.5 1.71429ZM18.15 10.2857C17.6806 10.2857 17.3 10.6695 17.3 11.1429V12C17.3 15.7811 14.2496 18.8571 10.5 18.8571C6.75044 18.8571 3.7 15.7811 3.7 12V11.1429C3.7 10.6695 3.31941 10.2857 2.85 10.2857C2.38059 10.2857 2 10.6695 2 11.1429V12C2 16.4372 5.36069 20.0966 9.65 20.5279V22.2857H7.95C7.48059 22.2857 7.1 22.6695 7.1 23.1429C7.1 23.6162 7.48059 24 7.95 24H13.05C13.5194 24 13.9 23.6162 13.9 23.1429C13.9 22.6695 13.5194 22.2857 13.05 22.2857H11.35V20.5279C15.6393 20.0964 19 16.437 19 12V11.1429C19 10.6695 18.6194 10.2857 18.15 10.2857Z" />
    </mask>
    <path
      d="M10.5 17.1429C7.68778 17.1429 5.4 14.8359 5.4 12V5.14286C5.4 2.307 7.68778 0 10.5 0C13.3122 0 15.6 2.307 15.6 5.14286V12C15.6 14.8359 13.3122 17.1429 10.5 17.1429ZM10.5 1.71429C8.62533 1.71429 7.1 3.25243 7.1 5.14286V12C7.1 13.8904 8.62533 15.4286 10.5 15.4286C12.3747 15.4286 13.9 13.8904 13.9 12V5.14286C13.9 3.25243 12.3747 1.71429 10.5 1.71429ZM18.15 10.2857C17.6806 10.2857 17.3 10.6695 17.3 11.1429V12C17.3 15.7811 14.2496 18.8571 10.5 18.8571C6.75044 18.8571 3.7 15.7811 3.7 12V11.1429C3.7 10.6695 3.31941 10.2857 2.85 10.2857C2.38059 10.2857 2 10.6695 2 11.1429V12C2 16.4372 5.36069 20.0966 9.65 20.5279V22.2857H7.95C7.48059 22.2857 7.1 22.6695 7.1 23.1429C7.1 23.6162 7.48059 24 7.95 24H13.05C13.5194 24 13.9 23.6162 13.9 23.1429C13.9 22.6695 13.5194 22.2857 13.05 22.2857H11.35V20.5279C15.6393 20.0964 19 16.437 19 12V11.1429C19 10.6695 18.6194 10.2857 18.15 10.2857Z"
      fill="#FF634A"
    />
    <path
      d="M9.65 20.5279H13.65V16.91L10.0502 16.548L9.65 20.5279ZM9.65 22.2857V26.2857H13.65V22.2857H9.65ZM11.35 22.2857H7.35V26.2857H11.35V22.2857ZM11.35 20.5279L10.9496 16.548L7.35 16.9102V20.5279H11.35ZM10.5 17.1429V13.1429C9.92841 13.1429 9.4 12.6583 9.4 12H5.4H1.4C1.4 17.0134 5.44714 21.1429 10.5 21.1429V17.1429ZM5.4 12H9.4V5.14286H5.4H1.4V12H5.4ZM5.4 5.14286H9.4C9.4 4.48451 9.92841 4 10.5 4V0V-4C5.44714 -4 1.4 0.129485 1.4 5.14286H5.4ZM10.5 0V4C11.0716 4 11.6 4.48451 11.6 5.14286H15.6H19.6C19.6 0.129485 15.5529 -4 10.5 -4V0ZM15.6 5.14286H11.6V12H15.6H19.6V5.14286H15.6ZM15.6 12H11.6C11.6 12.6583 11.0716 13.1429 10.5 13.1429V17.1429V21.1429C15.5529 21.1429 19.6 17.0134 19.6 12H15.6ZM10.5 1.71429V-2.28571C6.38469 -2.28571 3.1 1.07491 3.1 5.14286H7.1H11.1C11.1 5.42994 10.866 5.71429 10.5 5.71429V1.71429ZM7.1 5.14286H3.1V12H7.1H11.1V5.14286H7.1ZM7.1 12H3.1C3.1 16.0679 6.38469 19.4286 10.5 19.4286V15.4286V11.4286C10.866 11.4286 11.1 11.7129 11.1 12H7.1ZM10.5 15.4286V19.4286C14.6153 19.4286 17.9 16.0679 17.9 12H13.9H9.9C9.9 11.7129 10.134 11.4286 10.5 11.4286V15.4286ZM13.9 12H17.9V5.14286H13.9H9.9V12H13.9ZM13.9 5.14286H17.9C17.9 1.07491 14.6153 -2.28571 10.5 -2.28571V1.71429V5.71429C10.134 5.71429 9.9 5.42994 9.9 5.14286H13.9ZM18.15 10.2857V6.28571C15.44 6.28571 13.3 8.49199 13.3 11.1429H17.3H21.3C21.3 12.847 19.9212 14.2857 18.15 14.2857V10.2857ZM17.3 11.1429H13.3V12H17.3H21.3V11.1429H17.3ZM17.3 12H13.3C13.3 13.6036 12.0089 14.8571 10.5 14.8571V18.8571V22.8571C16.4902 22.8571 21.3 17.9586 21.3 12H17.3ZM10.5 18.8571V14.8571C8.99107 14.8571 7.7 13.6036 7.7 12H3.7H-0.3C-0.3 17.9586 4.50981 22.8571 10.5 22.8571V18.8571ZM3.7 12H7.7V11.1429H3.7H-0.3V12H3.7ZM3.7 11.1429H7.7C7.7 8.49199 5.56004 6.28571 2.85 6.28571V10.2857V14.2857C1.07878 14.2857 -0.3 12.847 -0.3 11.1429H3.7ZM2.85 10.2857V6.28571C0.139956 6.28571 -2 8.49199 -2 11.1429H2H6C6 12.847 4.62122 14.2857 2.85 14.2857V10.2857ZM2 11.1429H-2V12H2H6V11.1429H2ZM2 12H-2C-2 18.4873 2.91258 23.8706 9.24976 24.5079L9.65 20.5279L10.0502 16.548C7.80879 16.3226 6 14.3871 6 12H2ZM9.65 20.5279H5.65V22.2857H9.65H13.65V20.5279H9.65ZM9.65 22.2857V18.2857H7.95V22.2857V26.2857H9.65V22.2857ZM7.95 22.2857V18.2857C5.23996 18.2857 3.1 20.492 3.1 23.1429H7.1H11.1C11.1 24.847 9.72122 26.2857 7.95 26.2857V22.2857ZM7.1 23.1429H3.1C3.1 25.7937 5.23996 28 7.95 28V24V20C9.72122 20 11.1 21.4387 11.1 23.1429H7.1ZM7.95 24V28H13.05V24V20H7.95V24ZM13.05 24V28C15.76 28 17.9 25.7937 17.9 23.1429H13.9H9.9C9.9 21.4387 11.2788 20 13.05 20V24ZM13.9 23.1429H17.9C17.9 20.492 15.76 18.2857 13.05 18.2857V22.2857V26.2857C11.2788 26.2857 9.9 24.847 9.9 23.1429H13.9ZM13.05 22.2857V18.2857H11.35V22.2857V26.2857H13.05V22.2857ZM11.35 22.2857H15.35V20.5279H11.35H7.35V22.2857H11.35ZM11.35 20.5279L11.7504 24.5078C18.0874 23.8702 23 18.4872 23 12H19H15C15 14.3868 13.1913 16.3225 10.9496 16.548L11.35 20.5279ZM19 12H23V11.1429H19H15V12H19ZM19 11.1429H23C23 8.49199 20.86 6.28571 18.15 6.28571V10.2857V14.2857C16.3788 14.2857 15 12.847 15 11.1429H19Z"
      fill="#FF534A"
      mask="url(#voice-mic-muted-mask)"
    />
    <line x1="1.06066" y1="2" x2="20" y2="20.9393" stroke="#FF534A" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/**
 * Figma `icon / voice-4-bars` (`4936:3598`) — four rounded bars at heights
 * 6/16/10/6 inside the 16×16 art box, centered in a 24×24 frame. Animates
 * from each bar's vertical center when `active`.
 */
const VoiceBarsIcon: React.FC<{ active: boolean; color: string }> = ({ active, color }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden className={active ? 'voice-bars-active' : undefined}>
    <g transform="translate(4 4)" fill={color}>
      <rect className="voice-bar voice-bar-1" x="0" y="5" width="2" height="6" rx="1" />
      <rect className="voice-bar voice-bar-2" x="4.66" y="0" width="2" height="16" rx="1" />
      <rect className="voice-bar voice-bar-3" x="9.33" y="3" width="2" height="10" rx="1" />
      <rect className="voice-bar voice-bar-4" x="14" y="5" width="2" height="6" rx="1" />
    </g>
  </svg>
);

const SendIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M5 12h12M13 7l5 5-5 5"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SpinnerIcon: React.FC<{ color: string }> = ({ color }) => (
  <span
    className="voice-input-bar-spinner"
    aria-hidden
    style={{
      width: 18,
      height: 18,
      border: `2px solid ${color}`,
      borderTopColor: 'transparent',
      borderRadius: '50%',
      flexShrink: 0,
    }}
  />
);

const CONTROL_BAR_CSS = `
.voice-input-bar-field::placeholder {
  color: #9CA3AF;
  opacity: 1;
}
.voice-input-bar-field:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.voice-input-bar-mic:not(:disabled):hover {
  opacity: 0.75;
}
.voice-input-bar-action:hover {
  transform: translateY(-1px);
}
.voice-input-bar-action:active {
  transform: translateY(0);
}
.voice-input-bar-spinner {
  animation: voice-input-spin 0.75s linear infinite;
}
@keyframes voice-input-spin {
  to { transform: rotate(360deg); }
}
.voice-bar {
  transform-box: fill-box;
  transform-origin: center;
}
.voice-bars-active .voice-bar {
  animation: voice-bar-pulse 0.9s ease-in-out infinite;
}
.voice-bars-active .voice-bar-1 { animation-delay: 0ms; }
.voice-bars-active .voice-bar-2 { animation-delay: 120ms; }
.voice-bars-active .voice-bar-3 { animation-delay: 240ms; }
.voice-bars-active .voice-bar-4 { animation-delay: 360ms; }
@keyframes voice-bar-pulse {
  0%, 100% { transform: scaleY(0.4); }
  50% { transform: scaleY(1); }
}
@media (max-width: 759.98px) {
  .voice-input-bar-action {
    width: 44px !important;
    min-width: 44px !important;
    height: 44px !important;
    padding: 0 !important;
  }
  .voice-input-bar-action-label {
    display: none;
  }
}
`;

export default ControlBar;
